import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabaseClient";
import { useToast } from "@/hooks/use-toast";
import { useShopifyIntegrationStatus } from "@/hooks/useShopifyIntegrationStatus";

const ShopifyIntegrationsPage = () => {
  const { adminSlug } = useAuth();
  const { toast } = useToast();
  const { slug: urlSlug } = useParams<{ slug: string }>();
  const {
    integration,
    loading: loadingStatus,
    error: statusError,
    refresh: refreshStatus,
  } = useShopifyIntegrationStatus();

  const [shopDomain, setShopDomain] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [loadingAction, setLoadingAction] = useState(false);
  const [testing, setTesting] = useState(false);

  const normalizedSlug = useMemo(() => adminSlug ?? urlSlug ?? "", [adminSlug, urlSlug]);

  const normalizeShopInput = (value: string) => {
    let cleaned = value.trim().toLowerCase();
    cleaned = cleaned.replace(/^https?:\/\//, "");
    cleaned = cleaned.split("/")[0] ?? "";
    cleaned = cleaned.replace(/\s+/g, "");
    cleaned = cleaned.replace(/\.myshopify\.com$/, "");
    if (!cleaned) return "";
    return `${cleaned}.myshopify.com`;
  };

  const getToken = async () => {
    const { data, error } = await supabase.auth.getSession();
    if (error || !data?.session?.access_token) {
      throw new Error("Sessão inválida ou expirada.");
    }
    return data.session.access_token;
  };

  const handleTest = async () => {
    if (!integration?.shop_domain) return;
    setTesting(true);
    try {
      const token = await getToken();
      const res = await fetch(`/api/shopify/test?adminSlug=${encodeURIComponent(normalizedSlug)}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const json = await res.json();
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || "Falha ao testar integração.");
      }
      toast({
        title: "Conexão ok",
        description: json?.shop?.name || integration.shop_domain,
      });
    } catch (err) {
      toast({
        title: "Teste falhou",
        description: (err as Error).message ?? "Erro ao testar integração.",
        variant: "destructive",
      });
    } finally {
      setTesting(false);
    }
  };

  const handleConnect = async () => {
    const normalized = normalizeShopInput(shopDomain);
    if (!normalized) {
      toast({ title: "Informe um domínio .myshopify.com válido", variant: "destructive" });
      return;
    }
    if (!clientId.trim()) {
      toast({ title: "Informe o Client ID", variant: "destructive" });
      return;
    }
    if (!clientSecret.trim()) {
      toast({ title: "Informe o Client Secret", variant: "destructive" });
      return;
    }
    setLoadingAction(true);
    try {
      const token = await getToken();
      const res = await fetch("/api/shopify/custom/connect", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          shopDomain: normalized,
          adminSlug: normalizedSlug,
          clientId: clientId.trim(),
          clientSecret: clientSecret.trim(),
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json?.error || "Erro ao conectar.");
      }
      toast({
        title: "Shopify conectado",
        description: json?.shopDomain || normalized,
      });
      setClientId("");
      setClientSecret("");
      await refreshStatus({ force: true });
    } catch (err) {
      toast({
        title: "Erro ao conectar Shopify",
        description: (err as Error).message ?? "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setLoadingAction(false);
    }
  };

  const handleDisconnect = async () => {
    if (!integration) return;
    setLoadingAction(true);
    try {
      const token = await getToken();
      const res = await fetch("/api/shopify/custom/disconnect", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ shopDomain: integration.shop_domain }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Erro ao desconectar.");
      toast({ title: "Integração removida" });
      await refreshStatus({ force: true });
    } catch (err) {
      toast({
        title: "Erro ao desconectar",
        description: (err as Error).message ?? "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setLoadingAction(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      {!integration && (
        <Card>
          <CardHeader>
            <CardTitle>Shopify</CardTitle>
            <CardDescription>Conecte sua loja Shopify usando Client ID e Client Secret do app (Dev Dashboard).</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="shop-domain">Domínio da loja (.myshopify.com)</Label>
              <Input
                id="shop-domain"
                placeholder="minhaloja.myshopify.com"
                value={shopDomain}
                onChange={(e) => setShopDomain(e.target.value)}
                disabled={loadingAction}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="client-id">Client ID</Label>
              <Input
                id="client-id"
                type="text"
                placeholder="Client ID"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                disabled={loadingAction}
              />
              <p className="text-xs text-muted-foreground">
                Use o Client ID do app criado no Admin da Shopify.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="api-secret">Client Secret</Label>
              <Input
                id="api-secret"
                type="password"
                placeholder="Client Secret"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                disabled={loadingAction}
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleConnect} disabled={loadingAction || !shopDomain.trim()}>
                {loadingAction ? "Conectando..." : "Conectar Shopify"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Status da integração</CardTitle>
          <CardDescription>Verifique se a sua loja Shopify está ativa.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loadingStatus ? (
            <p className="text-sm text-muted-foreground">Carregando status...</p>
          ) : integration ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-xl border border-emerald-200 bg-emerald-50/80 px-4 py-3 shadow-[0_12px_35px_-20px_rgba(16,185,129,0.6)]">
                <div className="flex items-center gap-3">
                  <span className="relative flex h-3 w-3">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                    <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-500" />
                  </span>
                  <div className="space-y-1">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-emerald-700 font-semibold">
                      Status
                    </p>
                    <p className="text-sm font-semibold text-emerald-900">
                      Conectado em{" "}
                      <span className="font-mono text-emerald-900">{integration.shop_domain}</span>
                    </p>
                  </div>
                </div>
                <span className="rounded-full border border-emerald-200 bg-white/70 px-3 py-1 text-xs font-semibold text-emerald-700 shadow-sm">
                  Ativo
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={handleTest} disabled={testing}>
                  {testing ? "Testando..." : "Testar conexão"}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleConnect}
                  disabled={loadingAction || testing}
                  title="Reinstalar script e renovar token"
                >
                  Reinstalar script
                </Button>
                <Button variant="outline" onClick={handleDisconnect} disabled={loadingAction}>
                  Desconectar
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {statusError ? statusError : "Nenhuma loja conectada."}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ShopifyIntegrationsPage;

